import { useState, useCallback } from "react";

import DatePicker from "../Date-Picker";
import TextInput from "../Text-Input";
import { SingleSelect } from "../Select";
import { Option } from "../Select/types";

import {
  CommonProps,
  Props,
  DateProps,
  PasswordProps,
  NumericProps,
  TextProps,
} from "./types";

interface SelectWrapperProps<T extends number | string> extends CommonProps {
  options?: Option<T>[];
  allowedValues?: T[];
  inputRef: React.RefObject<HTMLInputElement>;
}

function SelectRefWrapper<T extends number | string = number | string>(
  props: SelectWrapperProps<T>
) {
  const { inputRef, ...rest } = props;
  const [value, setValue] = useState<T | null>(
    (inputRef?.current?.value || null) as T | null
  );

  const updateValue = useCallback(
    (value: T) => {
      setValue(value);
      const { current } = inputRef || {};
      if (current && current?.value !== value) {
        current.value = value as string;
        current.select();
      }
    },
    [inputRef, setValue]
  );

  const SelectBox = (
    // @ts-ignore
    <SingleSelect<T> selected={value} onChange={updateValue} {...rest} />
  );
  return (
    <>
      {SelectBox}
      <input hidden ref={inputRef} />
    </>
  );
}

function InputField(props: NumericProps): React.ReactElement;
function InputField(props: PasswordProps): React.ReactElement;
function InputField(props: DateProps): React.ReactElement;
function InputField<T extends number | string = number | string>(
  props: TextProps<T>
): React.ReactElement;
function InputField<T extends number | string = number | string>(
  props: Props<T>
): React.ReactElement;
function InputField<T extends number | string = number | string>({
  type,
  ...props
}: Props<T>) {
  switch (type) {
    case "date":
      return <DatePicker {...(props as Exclude<DateProps, "type">)} />;
    case "numeric":
    case "number":
      return (
        <TextInput {...(props as Exclude<NumericProps, "type">)} type="text" />
      );
    case "password":
      return (
        <TextInput
          {...(props as Exclude<PasswordProps, "type">)}
          type="password"
        />
      );
    case "text":
    case "char":
      if (
        Array.isArray(props["allowedValues"]) ||
        Array.isArray(props["options"])
      ) {
        if ("inputRef" in props) {
          return <SelectRefWrapper {...props} />;
        } else {
          const { value, onChange, ...rest } = props;

          return (
            // @ts-ignore
            <SingleSelect<T> selected={value} onChange={onChange} {...rest} />
          );
        }
      } else {
        return (
          <TextInput {...(props as Exclude<TextProps, "type">)} type="text" />
        );
      }
    default:
      return null;
  }
}

export default InputField;
